import { Head, useForm } from "@inertiajs/react"
import { useRef, useState } from "react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerSettingsProfilePath } from "@/routes"

interface PartnerProfile {
  uid: string
  name: string
  billing_email: string | null
  // Pre-populated server-side from PartnerAccount#signup_email (the
  // explicit value if set, otherwise the first invited user's email
  // — the "signup email" referenced in IMP-213). The partner can
  // overwrite before saving; the column update persists whatever
  // they submit.
  support_email: string | null
  contact_phone: string | null
  logo_url: string | null
  logo_wordmark_url: string | null
}

export default function ProfileSettings({
  partner,
}: {
  partner: PartnerProfile
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const wordmarkRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [wordmarkPreview, setWordmarkPreview] = useState<string | null>(null)

  const { data, setData, patch, processing, errors } = useForm({
    partner: {
      name: partner.name,
      billing_email: partner.billing_email ?? "",
      support_email: partner.support_email ?? "",
      contact_phone: partner.contact_phone ?? "",
      logo: null as File | null,
      logo_wordmark: null as File | null,
    },
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) {
      setData("partner", { ...data.partner, logo: e.target.files[0] })
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function handleWordmarkChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) {
      setData("partner", { ...data.partner, logo_wordmark: e.target.files[0] })
      setWordmarkPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(partnerSettingsProfilePath(), { forceFormData: true })
  }

  return (
    <PartnerLayout>
      <Head title="Settings — Details" />
      <PartnerSettingsLayout>
        <form onSubmit={handleSubmit}>
          <div className="flex max-w-md flex-col gap-5">
            <h2 className="text-sm font-semibold">Partner Details</h2>
            <div className="flex flex-col gap-1.5">
              <Label>Icon</Label>
              <div className="flex items-center gap-4">
                {preview || partner.logo_url ? (
                  <img
                    src={preview ?? partner.logo_url ?? ""}
                    alt={partner.name}
                    className="size-12 rounded-md border object-cover"
                  />
                ) : (
                  <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-md border text-xs font-semibold">
                    {partner.name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => fileRef.current?.click()}
                >
                  {partner.logo_url || preview ? "Change icon" : "Upload icon"}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Logo</Label>
              <div className="flex items-center gap-4">
                {wordmarkPreview || partner.logo_wordmark_url ? (
                  <img
                    src={wordmarkPreview ?? partner.logo_wordmark_url ?? ""}
                    alt={`${partner.name} logo`}
                    className="h-12 w-auto max-w-[200px] rounded-md border object-contain p-1"
                  />
                ) : (
                  <div className="bg-muted text-muted-foreground flex h-12 w-32 items-center justify-center rounded-md border text-xs font-semibold">
                    No logo
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => wordmarkRef.current?.click()}
                >
                  {partner.logo_wordmark_url || wordmarkPreview
                    ? "Change logo"
                    : "Upload logo"}
                </Button>
                <input
                  ref={wordmarkRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleWordmarkChange}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="workspace-name">Workspace name</Label>
              <Input
                id="workspace-name"
                value={data.partner.name}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="billing-email">Billing email</Label>
              <Input
                id="billing-email"
                type="email"
                value={data.partner.billing_email}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    billing_email: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="support-email">
                Support email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="support-email"
                type="email"
                required
                value={data.partner.support_email}
                onChange={(e) =>
                  setData("partner", {
                    ...data.partner,
                    support_email: e.target.value,
                  })
                }
              />
              <InputError
                message={(errors as Record<string, string>)?.support_email}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" className="rounded-sm" loading={processing}>
                Save changes
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
